// kendo
import { orderBy } from "@progress/kendo-data-query";
import { Chart, ChartSeries, ChartSeriesItem, ChartLegend, SeriesClickEvent } from "@progress/kendo-react-charts";
// api
import { OpggsApplicationTrackingTypeEnum } from "../../../../api/Client";
// common
import Help from "../../../../common/help/HelpView";
// me
import { IFilterViewModel, IRootViewModel } from "../models/models";

interface IProps {
    vm: IRootViewModel;
    onSeriesClick: (values: IFilterViewModel) => void;
}

export default function ChartView(props: IProps) {
    const { vm, onSeriesClick } = props;

    // sort application type summary items by name ascending
    const sortedTypeSummaryItems = orderBy(vm.applicationTrackingTypeSummary, [{ field: "applicationTrackingTypeName", dir: "asc" }]);
    // sort application status summary items by public status display order
    const sortedStatusSummaryItems = orderBy(vm.applicationTrackingStatusSummary, [{ field: "displayOrder", dir: "asc" }]);

    function handleSeriesClick(e: SeriesClickEvent) {
        onSeriesClick({ applicationTrackingType: e.dataItem.applicationTrackingType, isCompleted: vm.isCompleted });
    }

    function labelContent(e: any): string {
        if (!e || !e.value || e.value === 0) return "";
        return `${e.category}: ${e.value}`;
    }

    function legendLabelContent(e: any): string {
        return `${e.text}: ${e.value}`;
    }

    function getTypeSummaryChartDescription(): string {
        const baseDescription =
            "A pie chart displaying counts of " + (vm.isCompleted ? "Applications completed within the last 12 months" : "in progress Applications") + ", grouped by Application Type.";
        if (sortedTypeSummaryItems.length === 0) return baseDescription;

        const typeSummaryResults = sortedTypeSummaryItems.map((i) => `${i.applicationTrackingTypeName}: ${i.total}`).join("; ");
        return baseDescription + " The results are: " + typeSummaryResults + ".";
    }

    function getTypeSummaryChart(legendPosition: "bottom" | "right") {
        return (
            <Chart transitions={false} onSeriesClick={handleSeriesClick} style={{ height: 600 }}>
                <ChartLegend position={legendPosition} orientation="vertical" labels={{ content: legendLabelContent }} />
                <ChartSeries>
                    <ChartSeriesItem
                        type="pie"
                        data={sortedTypeSummaryItems}
                        categoryField="applicationTrackingTypeName"
                        field="total"
                        colorField="applicationTrackingTypeColour"
                        labels={{ visible: true, content: labelContent }}
                    />
                </ChartSeries>
            </Chart>
        );
    }

    function getStatusSummaryChartDescription(): string {
        const type = vm.applicationTrackingTypes.find((t) => t.id === vm.applicationTrackingType);
        const baseDescription =
            (vm.isCompleted
                ? `A pie chart displaying counts of ${type?.name} Applications completed within the last 12 months`
                : `A bar chart displaying counts of in progress ${type?.name} Applications`) + ", grouped by Application Status.";
        if (sortedStatusSummaryItems.length === 0) return baseDescription;

        const statusSummaryResults = sortedStatusSummaryItems.map((i) => `${i.applicationStatusName}: ${i.total}`).join("; ");
        return baseDescription + " The results are: " + statusSummaryResults + ".";
    }

    function getInProgressStatusSummaryChart() {
        return (
            <Chart transitions={false} style={{ minWidth: 400 }}>
                {/* <ChartLegend position='right' orientation="vertical" /> - there is no legend for this chart */}
                <ChartSeries>
                    <ChartSeriesItem type="bar" data={sortedStatusSummaryItems} categoryField="applicationStatusName" field="total" labels={{ visible: true }} />
                </ChartSeries>
            </Chart>
        );
    }

    function getCompletedStatusSummaryChart(legendPosition: "bottom" | "right") {
        return (
            <Chart transitions={false} style={{ height: 600 }}>
                <ChartLegend position={legendPosition} orientation="vertical" labels={{ content: legendLabelContent }} />
                <ChartSeries>
                    <ChartSeriesItem
                        type="pie"
                        data={sortedStatusSummaryItems}
                        categoryField="applicationStatusName"
                        field="total"
                        colorField="applicationStatusColour"
                        labels={{ visible: true, content: labelContent }}
                    />
                </ChartSeries>
            </Chart>
        );
    }

    // view
    return (
        <>
            {/* chart heading */}
            <div className="d-flex flex-wrap gap-2 mb-2">
                <h2 id="chartTitle" className="mb-0">
                    {vm.isCompleted ? "Completed Applications (Last 12 Months)" : "Applications in Progress"}
                </h2>
                <Help id="chartDescription" title="What does this chart show?">
                    {vm.applicationTrackingType ? getStatusSummaryChartDescription() : getTypeSummaryChartDescription()}
                </Help>
            </div>

            {/* note on responsiveness - pie charts render better when moving the legend, where-as the bar charts render better by setting a minwidth + x-scroll */}

            {/* type summary chart */}
            {!vm.applicationTrackingType && (
                <>
                    <div className="d-block d-sm-none" role="img" aria-labelledby="chartTitle" aria-describedby="chartDescription">
                        {getTypeSummaryChart("bottom")}
                    </div>
                    <div className="d-none d-sm-block" role="img" aria-labelledby="chartTitle" aria-describedby="chartDescription">
                        {getTypeSummaryChart("right")}
                    </div>
                </>
            )}

            {/* in progress status summary chart */}
            {vm.applicationTrackingType && !vm.isCompleted && (
                <>
                    <div style={{ overflowX: "auto" }} role="img" aria-labelledby="chartTitle" aria-describedby="chartDescription">
                        {getInProgressStatusSummaryChart()}
                    </div>
                    <p className="text-center">Not all Application Statuses are relevant to all Applications.</p>
                </>
            )}

            {/* completed status summary chart */}
            {vm.applicationTrackingType && vm.isCompleted && (
                <>
                    <div className="d-block d-sm-none" role="img" aria-labelledby="chartTitle" aria-describedby="chartDescription">
                        {getCompletedStatusSummaryChart("bottom")}
                    </div>
                    <div className="d-none d-sm-block" role="img" aria-labelledby="chartTitle" aria-describedby="chartDescription">
                        {getCompletedStatusSummaryChart("right")}
                    </div>
                </>
            )}

            {(vm.applicationTrackingType === OpggsApplicationTrackingTypeEnum.AcreageReleasePetroleum || vm.applicationTrackingType === OpggsApplicationTrackingTypeEnum.AcreageReleaseGhg) && (
                <p className="text-center">
                    Details of individual Acreage Release Area Applications are not visible in the Public Portal. The number of Acreage Release Area Applications at different Application Statuses can
                    be viewed in the above chart.
                </p>
            )}
            {vm.applicationTrackingType === OpggsApplicationTrackingTypeEnum.ChangeInControl && (
                <p className="text-center">
                    Details of individual Change in Control Applications are not visible in the Public Portal. The number of Change in Control Applications at different Application Statuses can be
                    viewed in the above chart.
                </p>
            )}
        </>
    );
}
